import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Button, Dialog, Flex, TextField } from '@radix-ui/themes'
import { Ops } from '@willie/schemas'
import { useTRPC } from '~/utils/trpc'
import { FieldShell } from '~/components/FieldShell'
import { MessageCallout } from '~/components/MessageCallout'
import { ContactPicker } from '~/features/contacts/components/ContactPicker'
import { ClientPhotoCheckbox } from '~/features/installations/components/ClientPhotoCheckbox'
import type { StationDetail } from '../types'

const toAdoptionBatch = (
  station: StationDetail,
  fields: { plannedInstallationDate: string } & Pick<
    Ops.StationInstallations.BatchSite,
    'siteContactId' | 'installationContactId'
  > &
    Pick<Ops.StationInstallations.BatchInstallation, 'pictureFromClientRequired'>
): Ops.StationInstallations.BatchCreateInput => ({
  orgId: station.org.id,
  source: 'station',
  kickoffDate: null,
  // `z.string().date()` rejects the empty string an untouched date input holds.
  plannedInstallationDate: fields.plannedInstallationDate || null,
  sites: [
    {
      siteId: station.site.id,
      siteName: station.site.name,
      siteContactId: fields.siteContactId,
      installationContactId: fields.installationContactId,
      installations: [
        {
          station: { mode: 'adopt', waterStationId: station.id },
          pictureFromClientRequired: fields.pictureFromClientRequired
        }
      ]
    }
  ]
})

interface CreateInstallationDialogProps {
  station: StationDetail
  children: ReactNode
}

export const CreateInstallationDialog = ({ station, children }: CreateInstallationDialogProps) => {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [plannedInstallationDate, setPlannedInstallationDate] = useState('')
  const [siteContactId, setSiteContactId] = useState<string | null>(null)
  const [installationContactId, setInstallationContactId] = useState<string | null>(null)
  const [pictureFromClientRequired, setPictureFromClientRequired] = useState(false)

  const createMutation = useMutation(
    trpc.ops.stationInstallations.batchCreate.mutationOptions({
      onSuccess: ({ installationIds }) => {
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
        queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.listSiteStations.queryKey() })
        navigate({
          to: '/installations/$installationId',
          params: { installationId: String(installationIds[0]) }
        })
      }
    })
  )

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setPlannedInstallationDate('')
      setSiteContactId(null)
      setInstallationContactId(null)
      setPictureFromClientRequired(false)
      createMutation.reset()
    }
    setOpen(next)
  }

  const pending = createMutation.isPending

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>Create installation</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Links a new installation to {station.name}. You will land on it to add the meter photo.
        </Dialog.Description>

        <Flex direction="column" gap="4">
          <FieldShell label="Expected installation date">
            <TextField.Root
              type="date"
              value={plannedInstallationDate}
              disabled={pending}
              onChange={(e) => setPlannedInstallationDate(e.target.value)}
            />
          </FieldShell>

          <ContactPicker
            orgId={station.org.id}
            value={siteContactId}
            disabled={pending}
            onChange={setSiteContactId}
          />

          <ContactPicker
            orgId={station.org.id}
            value={installationContactId}
            disabled={pending}
            onChange={setInstallationContactId}
            label="Installation contact"
          />

          <ClientPhotoCheckbox
            checked={pictureFromClientRequired}
            disabled={pending}
            onChange={setPictureFromClientRequired}
          />

          {createMutation.error ? <MessageCallout color="red" message={createMutation.error.message} /> : null}
        </Flex>

        <Flex justify="end" gap="2" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={pending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            variant="solid"
            loading={pending}
            onClick={() =>
              createMutation.mutate(
                toAdoptionBatch(station, {
                  plannedInstallationDate,
                  siteContactId,
                  installationContactId,
                  pictureFromClientRequired
                })
              )
            }
          >
            Create installation
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
